// cne-info.jsx — 「CNE 資訊中心」, the in-app regulatory reference.
//
// ─────────────────────────────────────────────────────────────────────────
//  WHY THIS IS NOT THE WEBSITE'S ARTICLES, COPIED IN
//
//  The marketing site (site/pages/*) already carries 12 long-form SEO pages.
//  Duplicating that prose here would (a) read badly — in an app a nurse is
//  LOOKING SOMETHING UP, not settling in to read, (b) split the SEO value of
//  the pages that actually rank, and (c) create a second copy of every
//  regulatory number to drift out of date.
//
//  So this screen does the one thing the website structurally cannot: it binds
//  the rules to THIS nurse's dates. The site can only say "an RN needs 45
//  points"; here we say "your cycle runs 1 Jul 2026 – 30 Jun 2029 and you are
//  21 points short". Everything else is a short answer card with a "read more"
//  link out to the canonical page.
//
//  ‼️ EVERY regulatory figure comes from CNE_FACTS (cne-facts.js) — the SAME
//  module the website loads. Never hard-code a number in a card; if it is not
//  in CNE_FACTS, add it there so both surfaces move together.
//
//  Anything not settled by the published NCHK letter carries
//  CNE_FACTS.pendingNote. The Mandatory CNE System manual is still unpublished,
//  so several operational details genuinely are not knowable yet, and saying so
//  is the trustworthy answer.
// ─────────────────────────────────────────────────────────────────────────

// Canonical site slugs a card may deep-link to. Kept as an explicit list so a
// typo becomes a build failure (site/build.mjs asserts every slug used here
// exists in seo.config.mjs's PAGES) rather than a 404 shipped to users.
const CNE_INFO_SLUGS = [
  'cne-guide', 'mandatory-cne-2026', 'free-cne-courses', 'online-cne-courses',
  'cne-points-calculator', 'bls-acls-pals-cne', 'nurse-licence-renewal',
  'cne-certificate-guide', 'specialist-cne-courses', 'cne-faq',
  'cne-providers', 'cne-app',
];

// Resolve a CNE_FACTS bilingual value ({zh,en}) to a string, falling back to a
// local implementation if data.js's L() hasn't published yet.
//
// ‼️ MODULE SCOPE ON PURPOSE. This used to be declared only inside
// cneInfoCards(), while CneInfoScreen's Administrators block also called `t(…)`
// — an out-of-scope reference that threw `ReferenceError: t is not defined` on
// every render, unmounting the React tree and blanking the whole CNE 資訊中心
// page for signed-in users. `no-undef` in eslint.config.mjs now catches that
// class of bug; keep shared helpers here rather than re-declaring them inside
// one function. The name is deliberately specific (not `t`) because build.mjs
// auto-exposes every top-level .jsx binding onto `window`.
function cneFactText(v, lang) {
  return (typeof L === 'function') ? L(v, lang) : ((v && (v[lang] || v.en)) || '');
}

// One question → one 30-second answer. `a` is a function of (F, lang) where F
// is CNE_FACTS, so a number can only ever come from the shared module.
// `tags` feed the plain-text search; `slug` is the deep link for "read more".
function cneInfoCards() {
  const F = (typeof CNE_FACTS !== 'undefined' && CNE_FACTS) || {};
  const t = cneFactText;
  const pend = (lang) => t(F.pendingNote, lang);

  return [
    {
      id: 'points-required',
      slug: 'cne-points-calculator',
      tags: ['points', '學分', '分數', 'rn', 'en', '45', '30'],
      q: { en: 'How many CNE points do I need?', zh: '我需要幾多 CNE 學分？' },
      a: (lang) => lang === 'zh'
        ? `註冊護士（RN）每 ${F.cycleYears} 年週期需 ${F.rnPoints} 分；登記護士（EN）需 ${F.enPoints} 分。`
        : `A Registered Nurse needs ${F.rnPoints} points per ${F.cycleYears}-year cycle; an Enrolled Nurse needs ${F.enPoints}.`,
    },
    {
      id: 'hours-to-points',
      slug: 'cne-points-calculator',
      tags: ['hours', '小時', 'convert', '換算'],
      q: { en: 'How do learning hours convert to points?', zh: '學習時數如何換算成學分？' },
      a: (lang) => lang === 'zh'
        ? `${F.hoursPerPoint} 小時認可學習活動 = 1 分。`
        : `${F.hoursPerPoint} hour of an accredited learning activity = 1 point.`,
    },
    {
      id: 'indirect-cap',
      slug: 'cne-guide',
      tags: ['indirect', '間接', 'cap', '上限'],
      q: { en: 'Is there a cap on indirectly-related courses?', zh: '間接相關的課程有冇上限？' },
      a: (lang) => lang === 'zh'
        ? `有。與護理間接相關的課程,每個週期最多計 ${F.indirectCap} 分。`
        : `Yes — programmes only indirectly related to nursing are capped at ${F.indirectCap} points per cycle.`,
    },
    {
      id: 'mandatory-when',
      slug: 'mandatory-cne-2026',
      tags: ['mandatory', '強制', '2026', 'cycle', '週期'],
      q: { en: 'When does mandatory CNE start?', zh: '強制性 CNE 幾時開始？' },
      a: (lang) => lang === 'zh'
        ? `${t(F.mandatoryEffective, lang)}生效,首個週期至 ${t(F.firstCycleEnds, lang)}。依據護士管理局 ${t(F.letterDate, lang)} 的信件。`
        : `It takes effect on ${t(F.mandatoryEffective, lang)}, with the first cycle running to ${t(F.firstCycleEnds, lang)} (NCHK letter of ${t(F.letterDate, lang)}).`,
    },
    {
      id: 'mandatory-who',
      slug: 'mandatory-cne-2026',
      tags: ['mandatory', '強制', 'who', '邊個', 'expiry'],
      q: { en: 'Which certificate is the first one bound by it?', zh: '第一張受強制 CNE 約束的執業證書係邊張？' },
      a: (lang) => lang === 'zh'
        ? `於 ${t(F.firstMandatoryExpiry, lang)}到期的執業證書。較早到期的續期仍屬自願性質。`
        : `The practising certificate expiring on ${t(F.firstMandatoryExpiry, lang)}. Renewals falling due earlier remain voluntary.`,
    },
    {
      id: 'cycle-window',
      slug: 'nurse-licence-renewal',
      tags: ['cycle', '週期', 'window', '計算'],
      q: { en: 'How is my CNE cycle worked out?', zh: '我的 CNE 週期點計？' },
      a: (lang) => lang === 'zh'
        ? `由執業證書到期日往前推 6 個月作結,為期 ${F.cycleYears} 年。標準組別的週期由${t(F.cycleStart, lang)}開始。`
        : `It ends 6 months before your practising certificate expires and runs ${F.cycleYears} years. The standard cohort's cycle begins ${t(F.cycleStart, lang)}.`,
    },
    {
      id: 'pc-validity',
      slug: 'nurse-licence-renewal',
      tags: ['practising certificate', '執業證書', 'renewal', '續期'],
      q: { en: 'How long is a practising certificate valid?', zh: '執業證書有效期幾耐？' },
      a: (lang) => lang === 'zh'
        ? `${F.pcValidYears} 年,到期日為${t(F.pcExpiry, lang)}。`
        : `${F.pcValidYears} years, expiring ${t(F.pcExpiry, lang)}.`,
    },
    {
      id: 'administrator',
      slug: 'cne-providers',
      tags: ['administrator', '管理者', 'database', '資料庫'],
      q: { en: 'What is a CNE Administrator, and must I pick one?', zh: '咩係 CNE Administrator？一定要揀嗎？' },
      a: (lang) => lang === 'zh'
        ? `${t(F.cneAdministrator, lang)}。截至${t(F.administratorsAsAt, lang)},共有 ${(F.administrators || []).length} 間認可機構（見下方名單）。登記程序與費用尚未公布。${pend(lang)}`
        : `${t(F.cneAdministrator, lang)}. As at ${t(F.administratorsAsAt, lang)} there are ${(F.administrators || []).length} accredited bodies (listed below). How you register, and at what cost, has not been published. ${pend(lang)}`,
      link: { url: F.cneAdministratorListUrl, en: 'NCHK accredited list', zh: '護士管理局認可名單' },
    },
    {
      id: 'bls-points',
      slug: 'bls-acls-pals-cne',
      tags: ['bls', 'acls', 'pals', '急救', 'aha'],
      q: { en: 'How many points is a BLS or ACLS course worth?', zh: 'BLS、ACLS 課程有幾多分？' },
      a: (lang) => lang === 'zh'
        ? `AHA BLS Provider 為 ${F.blsPoints} 分,證書有效期兩年;ACLS／PALS ${t(F.aclsPalsRange, lang)}。${pend(lang)}`
        : `An AHA BLS Provider course is ${F.blsPoints} points and the card is valid two years; ACLS/PALS are ${t(F.aclsPalsRange, lang)}. ${pend(lang)}`,
    },
    {
      id: 'retention',
      slug: 'cne-certificate-guide',
      tags: ['certificate', '證書', 'keep', '保存', 'audit', '稽核'],
      q: { en: 'How long must I keep my certificates?', zh: '證書要保存幾耐？' },
      a: (lang) => lang === 'zh'
        ? `至少 ${F.retentionYears} 年。護士管理局可要求你出示證明文件。`
        : `At least ${F.retentionYears} years — the Council may ask you to produce supporting documents.`,
    },
    {
      id: 'free-courses',
      slug: 'free-cne-courses',
      tags: ['free', '免費', 'cost', '費用'],
      q: { en: 'Are there free CNE courses?', zh: '有冇免費 CNE 課程？' },
      a: (lang) => lang === 'zh'
        ? '有。「探索」可用「免費」及價格上限篩選,不少機構亦提供免費網上講座。'
        : 'Yes. Use the Free and price-ceiling filters in Discover; many providers also run free online talks.',
    },
    {
      id: 'online',
      slug: 'online-cne-courses',
      tags: ['online', '網上', '線上', 'zoom', 'self-study'],
      q: { en: 'Do online and self-study courses count?', zh: '網上及自學課程計唔計？' },
      a: (lang) => lang === 'zh'
        ? `計,只要屬認可活動。自學活動在 NCHK 附件 A 有獨立的「活動性質」分類。${pend(lang)}`
        : `They do, provided the activity is accredited. Self-study has its own nature-of-activity category on NCHK Annex A. ${pend(lang)}`,
    },
    {
      id: 'specialty',
      slug: 'specialist-cne-courses',
      tags: ['specialty', '專科', 'icu', 'apn'],
      q: { en: 'Where do I find specialty courses?', zh: '專科課程喺邊度搵？' },
      // Points at the filter sheet only. The Discover home used to carry a
      // "Browse by specialty" tile strip; it was removed, so naming it here
      // would send nurses looking for a control that no longer exists.
      a: (lang) => lang === 'zh'
        ? '在「探索」開啟篩選,用「專科」收窄結果。'
        : 'In Discover, open the filters and narrow by “Specialty”.',
    },
    {
      id: 'declare',
      slug: 'cne-guide',
      tags: ['annex', '附件', 'declare', '申報', 'export', '匯出'],
      q: { en: 'How do I declare my points at renewal?', zh: '續期時點樣申報學分？' },
      a: (lang) => lang === 'zh'
        ? '在「紀錄」一鍵匯出 NCHK 附件 A + B。附件 A 逐項列出活動,附件 B 為聲明。'
        : 'Export NCHK Annex A + B from the Record tab. Annex A itemises each activity; Annex B is the declaration.',
    },
    {
      id: 'bill',
      slug: 'mandatory-cne-2026',
      tags: ['law', '法例', 'gazette', '憲報'],
      q: { en: 'What is the legal basis?', zh: '法律依據係咩？' },
      a: (lang) => lang === 'zh'
        ? `相關條例草案於${t(F.billGazetted, lang)}刊憲。`
        : `The enabling bill was gazetted on ${t(F.billGazetted, lang)}.`,
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────────
// <CneInfoScreen>
// ─────────────────────────────────────────────────────────────────────────
function CneInfoScreen({ lang, setLang, profile, user, records = [], onBack, onProfileSaved }) {
  const [query, setQuery] = React.useState('');
  const [openId, setOpenId] = React.useState(null);
  // Which accredited administrator this nurse has registered with. Stored in
  // profiles.prefs (jsonb) — no migration, and it is a preference, not
  // regulatory data we are asserting on her behalf.
  const [adminKey, setAdminKey] = React.useState(() => profile?.prefs?.cneAdministrator?.key || null);
  const [adminBusy, setAdminBusy] = React.useState(false);
  const today = (typeof window !== 'undefined' && window.todayIso) ? window.todayIso() : '';
  const F = (typeof CNE_FACTS !== 'undefined' && CNE_FACTS) || {};
  // Same bilingual resolver the cards use — the Administrators block and the
  // pending-note footer below both call t(). See cneFactText's header: this
  // binding was missing, which blanked the entire screen.
  const t = cneFactText;
  const cards = React.useMemo(() => cneInfoCards(), []);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => {
      const hay = [
        L(c.q, 'en'), L(c.q, 'zh'), c.a('en'), c.a('zh'), ...(c.tags || []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [cards, query]);

  // ── Personalisation: the one thing the website cannot do ──
  // Renders per licence, so a dual-registered nurse sees both cycles. Three
  // states the data model already distinguishes must all read honestly:
  //   • no cycle dates set   → prompt, never a fake window
  //   • first_pc exempt      → "no requirement yet", never "0 of 45"
  //   • ordinary             → window + shortfall
  const licences = (typeof profileLicenses === 'function' ? profileLicenses(profile) : []) || [];

  // Persist the choice into profiles.prefs. Optimistic; reverts on failure so
  // the UI never claims a selection the server didn't accept.
  const saveAdmin = async (key) => {
    if (adminBusy) return;
    const prev = adminKey;
    setAdminKey(key);
    setAdminBusy(true);
    try {
      // setPrefs(userId, patch) shallow-merges into profiles.prefs.
      const uid = profile?.id || (await window.AuthAPI?.getSession?.())?.user?.id;
      if (!uid) { setAdminKey(prev); return; }
      const res = await window.DB?.setPrefs?.(uid, {
        cneAdministrator: key
          ? { key, selectedAt: (window.todayIso ? window.todayIso() : new Date().toISOString().slice(0, 10)) }
          : null,
      });
      if (res?.error) { setAdminKey(prev); return; }
      if (res?.data) onProfileSaved?.(res.data);
    } catch (e) {
      console.warn('saveAdmin failed', e);
      setAdminKey(prev);
    } finally {
      setAdminBusy(false);
    }
  };

  const openSite = (slug) => {
    const url = `${F.webAppUrl ? F.webAppUrl.replace('app.', 'www.') : 'https://www.vitalcne.com'}/${slug}`;
    if (window.Capacitor?.Plugins?.Browser?.open) window.Capacitor.Plugins.Browser.open({ url });
    else window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="scroll fade-in statusbar-capped" style={{ paddingBottom: 32 }}>
      <ScreenHeader
        title={lang === 'zh' ? 'CNE 資訊中心' : 'CNE information'}
        onBack={onBack}
        sticky
        actions={setLang ? <LangPill lang={lang} setLang={setLang} /> : null}
      />

      {/* Your cycle — facts bound to this nurse's dates. */}
      {licences.length > 0 && (
        <div style={{ padding: '4px 20px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {licences.map((lic) => {
            const prog = (typeof licenseProgress === 'function')
              ? licenseProgress(records, lic) : null;
            const win = prog && prog.window;
            const short = prog ? Math.max(0, prog.required - prog.earned) : 0;
            return (
              <div key={lic.key} className="card" style={{ padding: '12px 14px' }}>
                <div className="muted-2" style={{ fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                  {L(lic.role, lang)}
                </div>
                {!win || !win.start || !win.end ? (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {lang === 'zh'
                      ? '未設定執業證書日期,所以未能顯示你的週期。可在「個人檔案」補上。'
                      : 'Your practising-certificate dates aren’t set yet, so we can’t show your cycle. Add them in Profile.'}
                  </div>
                ) : lic.exempt ? (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {lang === 'zh'
                      ? `首次申請執業證書,今個週期沒有 CNE 學分要求。週期：${win.start} 至 ${win.end}。`
                      : `First practising certificate — no CNE requirement this cycle. Cycle: ${win.start} to ${win.end}.`}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {lang === 'zh'
                      ? `你的週期：${win.start} 至 ${win.end}。已取得 ${prog.earned} / ${prog.required} 分`
                      : `Your cycle: ${win.start} to ${win.end}. Earned ${prog.earned} of ${prog.required} points`}
                    {short > 0 && (
                      <strong>{lang === 'zh' ? `,尚欠 ${short} 分。` : ` — ${short} to go.`}</strong>
                    )}
                    {short === 0 && (lang === 'zh' ? ',已達標。' : ' — requirement met.')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Accredited CNE Administrators — the biggest information vacuum in the
          2026 changeover. The NAMES are firm (transcribed from the Council's
          PDF); the PROCESS is not published, so we say that plainly instead of
          inventing comparison columns (fees, turnaround, transferability) that
          would be confident and wrong. Selecting one records the nurse's own
          choice so she can find it again — it does not register her. */}
      {Array.isArray(F.administrators) && F.administrators.length > 0 && (
        <div style={{ padding: '12px 20px 0' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 2 }}>
            {lang === 'zh' ? '認可 CNE Administrator' : 'Accredited CNE Administrators'}
          </div>
          <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginBottom: 8 }}>
            {lang === 'zh'
              ? `截至${t(F.administratorsAsAt, lang)}的認可名單（認可期 ${t(F.administratorsPeriod, lang)}）。選擇只作個人記錄,不代表已完成登記。`
              : `The accredited list as at ${t(F.administratorsAsAt, lang)} (accreditation period ${t(F.administratorsPeriod, lang)}). Choosing one is a note to yourself — it does not register you.`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {F.administrators.map((a) => {
              const on = adminKey === a.key;
              return (
                <button key={a.key} type="button"
                  className="card"
                  aria-pressed={on}
                  disabled={adminBusy}
                  onClick={() => saveAdmin(on ? null : a.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                           textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer',
                           border: on ? '1px solid var(--primary)' : undefined,
                           background: on ? 'var(--primary-soft-2, var(--surface))' : undefined }}>
                  <Icon name={on ? 'check-circ' : 'shield'} size={16} stroke={1.8}
                    color={on ? 'var(--primary)' : 'var(--fg-3)'} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                    {/* Chinese only where the official name is confirmed —
                        otherwise the accredited English name, verbatim. */}
                    {(lang === 'zh' && a.name_zh) ? a.name_zh : a.name_en}
                    {lang === 'zh' && a.name_zh && (
                      <span className="muted-2" style={{ display: 'block', fontSize: 11 }}>{a.name_en}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="muted-2" style={{ fontSize: 11, marginTop: 6 }}>
            {t(F.pendingNote, lang)}
          </div>
        </div>
      )}

      <div style={{ padding: '12px 20px 6px' }}>
        <input
          className="field-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={lang === 'zh' ? '搜尋 CNE 問題…' : 'Search CNE questions…'}
          aria-label={lang === 'zh' ? '搜尋' : 'Search'}
        />
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((c) => {
          const open = openId === c.id;
          return (
            <div key={c.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <button type="button"
                onClick={() => setOpenId(open ? null : c.id)}
                aria-expanded={open}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                         background: 'transparent', border: 0, font: 'inherit', color: 'inherit',
                         textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{L(c.q, lang)}</span>
                <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>
                  <Icon name="chev-d" size={15} stroke={1.8} />
                </span>
              </button>
              {open && (
                <div style={{ padding: '0 14px 12px' }}>
                  <div style={{ fontSize: 13, lineHeight: 1.55 }}>{c.a(lang)}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {c.slug && (
                      <button type="button" className="filter-chip" onClick={() => openSite(c.slug)}>
                        {lang === 'zh' ? '深入閱讀' : 'Read more'}
                      </button>
                    )}
                    {c.link && (
                      <button type="button" className="filter-chip"
                        onClick={() => {
                          if (window.Capacitor?.Plugins?.Browser?.open) window.Capacitor.Plugins.Browser.open({ url: c.link.url });
                          else window.open(c.link.url, '_blank', 'noopener');
                        }}>
                        {lang === 'zh' ? c.link.zh : c.link.en}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="vital-empty" style={{ marginTop: 12 }}>
            <div className="vital-empty-icon"><Icon name="search" size={22} stroke={1.7} /></div>
            <div className="vital-empty-title">{lang === 'zh' ? '沒有相關條目' : 'Nothing matches'}</div>
            <div className="vital-empty-sub">
              {lang === 'zh' ? '試試其他關鍵字,或到網站查看完整指南。' : 'Try another term, or read the full guides on the website.'}
            </div>
          </div>
        )}
      </div>

      {/* Regulatory timeline — the "what changed and when" a nurse cannot get
          anywhere else. Past entries are dimmed so the eye lands on what is
          still ahead of her. Every row is a dated, sourced fact (see the guard
          on CNE_FACTS.timeline). */}
      {Array.isArray(F.timeline) && F.timeline.length > 0 && (
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>
            {lang === 'zh' ? '法規時間軸' : 'Regulatory timeline'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {F.timeline.slice().sort((a, b) => a.date.localeCompare(b.date)).map((ev) => {
              const past = ev.date < today;
              return (
                <div key={ev.id} style={{ display: 'flex', gap: 10, opacity: past ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 5,
                                   background: past ? 'var(--fg-3)' : 'var(--primary)' }} />
                    <span style={{ flex: 1, width: 1, background: 'var(--border)' }} />
                  </div>
                  <div style={{ paddingBottom: 14, minWidth: 0 }}>
                    <div className="mono muted-2" style={{ fontSize: 11 }}>{ev.date}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{L(ev.title, lang)}</div>
                    <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 2 }}>
                      {L(ev.note, lang)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Provenance. A compliance reference that doesn't say WHEN it was checked,
          or link the primary source, is asking to be trusted on faith. */}
      <div style={{ padding: '16px 20px 0' }} className="muted-2">
        <div style={{ fontSize: 11.5, lineHeight: 1.6 }}>
          {lang === 'zh'
            ? `資料最後更新：${F.lastUpdated}。所有規例以護士管理局公布為準。`
            : `Last updated ${F.lastUpdated}. The Nursing Council's own publications are the authority.`}
        </div>
        <button type="button" className="filter-chip" style={{ marginTop: 8 }}
          onClick={() => {
            const url = F.nchkUrl || 'https://www.nchk.org.hk';
            if (window.Capacitor?.Plugins?.Browser?.open) window.Capacitor.Plugins.Browser.open({ url });
            else window.open(url, '_blank', 'noopener');
          }}>
          {lang === 'zh' ? '護士管理局官網' : 'NCHK website'}
        </button>
      </div>
    </div>
  );
}

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { CNE_INFO_SLUGS, cneFactText, cneInfoCards, CneInfoScreen });
